/**
 * @typedef {Object} ModifierCore
 * @property {string} id
 * @property {string} name
 * @property {string} [description]
 * @property {number} ordinal
 * @property {boolean} isDefault
 * @property {Object} [payout] Money; unset for a free modifier.
 *
 * @typedef {Object} PartnerSkuModifier
 * @property {ModifierCore} core
 * @property {boolean} soldOut
 * @property {boolean} enabled
 * @property {boolean} published
 * @property {string} [partnerState]
 *
 * @typedef {Object} ModifierGroupCore
 * @property {string} id
 * @property {string} name
 * @property {string} [description]
 * @property {number} selectionsMin
 * @property {number} selectionsMax Always ≥ 1. Maximum "None" (no cap) compiles to the current
 *   option count on the client at save time (TDD 5.2); the server recomputes it for no-cap
 *   groups when modifiers are added or removed.
 * @property {boolean} allowsQuantities
 *
 * @typedef {Object} AttachedItemSummary
 * @property {string} itemId
 * @property {string} name
 *
 * @typedef {Object} PartnerSkuModifierGroup
 * @property {ModifierGroupCore} core
 * @property {number} venueId
 * @property {boolean} enabled
 * @property {boolean} published
 * @property {PartnerSkuModifier[]} modifiers
 * @property {string} [partnerState]
 * @property {number} attachedItemCount
 * @property {AttachedItemSummary[]} attachedItems
 * @property {string} unpublishReason One of the UnpublishReason enum values.
 *
 * @typedef {Object} CreateModifierInput
 * @property {string} name
 * @property {string} [description]
 * @property {number} ordinal
 * @property {boolean} isDefault
 * @property {boolean} [enabled]
 * @property {boolean} [soldOut]
 */

/**
 * The partner modifier contract as a single port. Callers depend on this shape,
 * never on httpClient or the in-memory store, so an operation can move from mock
 * to real without touching any component or page.
 *
 * @typedef {Object} ModifierGateway
 * @property {(venueId: number, group: {
 *   name: string, description?: string, selectionsMin: number, selectionsMax: number,
 *   allowsQuantities: boolean, modifiers?: CreateModifierInput[],
 * }) => Promise<PartnerSkuModifierGroup>} createModifierGroup
 * @property {(venueId: number, groupId: string, updates: {
 *   name?: string, description?: string, selectionsMin?: number, selectionsMax?: number,
 *   allowsQuantities?: boolean, enabled?: boolean, partnerState?: string, modifierOrder?: string[],
 * }) => Promise<PartnerSkuModifierGroup>} updateModifierGroup
 * @property {(venueId: number, filters?: { enabled?: boolean, published?: boolean }) =>
 *   Promise<PartnerSkuModifierGroup[]>} getModifierGroups
 * @property {(venueId: number, groupId: string, modifier: CreateModifierInput) =>
 *   Promise<PartnerSkuModifier>} createModifier
 * @property {(venueId: number, modifierId: string, updates: {
 *   name?: string, description?: string, ordinal?: number, isDefault?: boolean,
 *   soldOut?: boolean, enabled?: boolean, partnerState?: string,
 * }) => Promise<PartnerSkuModifier>} updateModifier
 * @property {(venueId: number, groupId: string, itemIds: string[]) => Promise<void>} linkModifierGroupToItems
 * @property {(venueId: number, groupId: string, itemId: string) => Promise<void>} unlinkModifierGroupFromItem
 */

export const UNPUBLISH_REASON = {
  UNSPECIFIED: 'UNPUBLISH_REASON_UNSPECIFIED',
  INSUFFICIENT_OPTIONS: 'UNPUBLISH_REASON_INSUFFICIENT_OPTIONS',
};
